import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { portfolioKeys } from '@/features/portfolio/api/keys'
import { portfolioService } from '@/features/portfolio/api/portfolioService'
import {
  DEFAULT_VALUE_HISTORY_PERIOD,
  type AnalyticsReport,
  type BonusDraft,
  type BuyDraft,
  type BuyReceipt,
  type CashDividendDraft,
  type DeletedProfile,
  type DeletedTransaction,
  type DividendReceipt,
  type HealthReport,
  type Holding,
  type ImportDraft,
  type ImportPreview,
  type ImportResult,
  type ImportSaveDraft,
  type IncomeReport,
  type PortfolioOverview,
  type PortfolioSummary,
  type ProfileCreateDraft,
  type ProfileUpdateDraft,
  type ProfitLossReport,
  type SellDraft,
  type SellReceipt,
  type ShareholderProfile,
  type SnapshotPoint,
  type TransactionEditDraft,
  type TransactionGroup,
  type TransactionHistory,
  type UpdatedTransaction,
  type ValueHistoryPeriod,
} from '@/schemas/portfolio'

/**
 * The portfolio query layer: ten reads, thirteen writes, and the invalidation
 * map that keeps them honest.
 *
 * ── The invalidation map ─────────────────────────────────────────────────────
 *
 * This is the part of the domain easiest to get subtly wrong, because a missed
 * invalidation is invisible in a test that only asserts the mutation resolved.
 * One recorded trade changes EIGHT reads — holdings, summary, overview,
 * transactions, profit-loss, analytics, income and health — so a buy that
 * refreshed only the ledger leaves yesterday's total sitting next to today's
 * new row, and the member has no way to tell which number is real.
 *
 *   MUTATION                      INVALIDATES
 *   ─────────────────────────────────────────────────────────────────────────
 *   useCreateProfile              shareholders() + book(undefined)
 *                                 The FIRST profile a member creates becomes
 *                                 primary, and until it existed every read
 *                                 answered 200-empty. So the primary scope's
 *                                 cached "you hold nothing" is now wrong.
 *   useUpdateProfile              shareholders()
 *                                 Name, type and demat only. No holding, total
 *                                 or ledger row moves, so the book is left
 *                                 alone rather than swept up.
 *   useSetPrimary                 shareholders() + book(undefined)
 *                                 The response describes ONE profile but every
 *                                 other profile's `is_primary` changed too
 *                                 (`unset_primary_except`), so the list must be
 *                                 refetched, not patched. And the primary scope
 *                                 now resolves to a different profile, so its
 *                                 cache is another profile's book. Explicitly
 *                                 scoped reads (`book(7)`) are untouched: their
 *                                 rows did not change.
 *   useDeleteProfile              all
 *                                 The deleted profile's scope is garbage, and
 *                                 deleting the primary PROMOTES the oldest
 *                                 remaining one — so the primary scope now
 *                                 points somewhere else too. Two scopes move
 *                                 and one of them is not named in the request,
 *                                 so the whole domain goes. `force: true` makes
 *                                 that MORE true, not less: it destroys every
 *                                 transaction the profile owned.
 *   useRecordBuy                  book(shareholderId)  ─┐
 *   useRecordSell                 book(shareholderId)   │ all nine reads for
 *   useRecordCashDividend         book(shareholderId)   │ that ONE profile
 *   useRecordBonus                book(shareholderId)  ─┘
 *                                 Scoped to the profile written to, so a member
 *                                 with four profiles refetches one book, not
 *                                 four. A cash dividend skips the position
 *                                 rebuild but still moves
 *                                 `summary.dividendReceived`, so it invalidates
 *                                 the same scope as the other three.
 *   useImportPreview              nothing
 *                                 The preview WRITES NOTHING — `/import/` is
 *                                 the only route that saves, and a preview is
 *                                 asserted not to touch a single row. Anything
 *                                 invalidated here would be a refetch bought
 *                                 with a request and paid for with a spinner.
 *   useImportSave                 book(shareholderId)  ─┐ the trades (JSON)
 *   useImportCommit               book(shareholderId)  ─┘ the WACC overlay
 *                                 The widest writes in the domain. One save
 *                                 can add buys, sells, bonus lots AND cash
 *                                 dividends in a single call, so it moves
 *                                 every read a single trade moves and then
 *                                 some. The overlay rewrites the stored
 *                                 average cost of every scrip it covers, which
 *                                 moves every read below but the ledger and
 *                                 income:
 *
 *                                   holdings      new rows, new quantities
 *                                   summary       totals, WACC, best/worst
 *                                   overview      both halves at once
 *                                   transactions  every one of the four arrays
 *                                   profitLoss    realised gain re-derived
 *                                   analytics     new cash flows → new XIRR,
 *                                                 re-ranked movers, new sector
 *                                                 weights
 *                                   income        `total_gross` moves on an
 *                                                 imported DIVIDEND row, and
 *                                                 `upcoming` follows which
 *                                                 companies are now held
 *                                   health        every concentration check is
 *                                                 a function of those weights,
 *                                                 and `record_quality` flips to
 *                                                 `act` the moment a MeroShare
 *                                                 import lands priceless buys
 *
 *                                 That is the whole `book(...)` scope, which is
 *                                 why one call covers each. Under-invalidating
 *                                 here is the worst bug on this screen: the
 *                                 member is told "42 imported" and then shown
 *                                 their pre-import portfolio.
 *                                 (`valueHistory` is swept along and cannot
 *                                 have moved — see the note in `keys.ts`.)
 *   useEditTransaction            all
 *   useDeleteTransaction          all
 *                                 Not a scope, deliberately. These two routes
 *                                 take no `shareholder_id` — ownership is
 *                                 derived from the row — and the ack is only
 *                                 `{id, group, deleted|updated}`, so NOTHING in
 *                                 the request or the response says which
 *                                 profile the row belonged to. Narrowing would
 *                                 mean guessing. And the blast radius is real:
 *                                 an edit runs `rebuild_position`, which
 *                                 replays FIFO and reprices every LATER sell's
 *                                 charges, CGT and realised profit — a stored
 *                                 sell profit has been observed moving from
 *                                 4399.53 to 9041.87 after only a buy's price
 *                                 changed. See BACKEND CHANGES NEEDED: echoing
 *                                 `shareholder_id` in the ack would let these
 *                                 two narrow to `book(id)`.
 */

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * The member's shareholder profiles, primary first then oldest first.
 *
 * An empty array is the first-run answer, not a failure: reads for a member
 * with no profile succeed with 200. Writes do NOT — they 404 — so anything that
 * records a transaction must gate on this being non-empty.
 */
export const useShareholders = (): UseQueryResult<ShareholderProfile[]> =>
  useQuery<ShareholderProfile[]>({
    queryKey: portfolioKeys.shareholders(),
    queryFn: () => portfolioService.listShareholders(),
  })

/** Open positions for one profile. */
export const useHoldings = (shareholderId?: number): UseQueryResult<Holding[]> =>
  useQuery<Holding[]>({
    queryKey: portfolioKeys.holdings(shareholderId),
    queryFn: () => portfolioService.holdings(shareholderId),
  })

/**
 * Portfolio totals, or `null` when the book is empty.
 *
 * `null` rather than a summary of zeros, because "no holdings" is a different
 * card, not a card full of zeros — and because the backend's own empty summary
 * is a mix of `0` and `null` (`holdingsCount: 0`, `totalMarketValue: null`)
 * that a component should not have to interpret twice.
 *
 * Narrowed in `select` rather than in the service: the service's job is to
 * report the contract faithfully, and `holdingsCount === 0` is a UI decision
 * about which frame to show.
 */
export const usePortfolioSummary = (
  shareholderId?: number,
): UseQueryResult<PortfolioSummary | null> =>
  useQuery<PortfolioSummary, Error, PortfolioSummary | null>({
    queryKey: portfolioKeys.summary(shareholderId),
    queryFn: () => portfolioService.summary(shareholderId),
    select: (summary) => (summary.holdingsCount === 0 ? null : summary),
  })

/**
 * Summary and holdings from ONE call.
 *
 * What anything needing both should read. Two separate reads are two
 * computations over a table a concurrent write may have moved between them, so
 * the header could legitimately show a total the rows beneath it do not add up
 * to — and nothing on screen would say which is stale.
 */
export const usePortfolioOverview = (shareholderId?: number): UseQueryResult<PortfolioOverview> =>
  useQuery<PortfolioOverview>({
    queryKey: portfolioKeys.overview(shareholderId),
    queryFn: () => portfolioService.overview(shareholderId),
  })

/** The whole ledger. Four arrays, newest first, unpaginated. */
export const useTransactions = (shareholderId?: number): UseQueryResult<TransactionHistory> =>
  useQuery<TransactionHistory>({
    queryKey: portfolioKeys.transactions(shareholderId),
    queryFn: () => portfolioService.transactions(shareholderId),
  })

/** Realised and unrealised profit, including fully-closed positions. */
export const useProfitLoss = (shareholderId?: number): UseQueryResult<ProfitLossReport> =>
  useQuery<ProfitLossReport>({
    queryKey: portfolioKeys.profitLoss(shareholderId),
    queryFn: () => portfolioService.profitLoss(shareholderId),
  })

/**
 * The value curve for one window, oldest point first.
 *
 * ⚠️ An EMPTY array is the normal answer today and it does NOT mean "no
 * holdings". `portfolio_daily_snapshot` is written by one management command
 * that nothing in mm-be schedules, so until that is wired up every member gets
 * `[]` — and history cannot be backfilled afterwards. A screen therefore needs
 * three states, not two:
 *
 *   holdings 0                     "No holdings yet" — the ordinary empty card
 *   holdings > 0, points 0         "We'll start tracking your value from
 *                                  today" — NOT an error, and NOT a chart
 *                                  flatlined at zero
 *   points 1                       a value badge; a line needs two points
 *   points >= 2                    the curve
 *
 * The period is part of the key, because the two answers genuinely differ:
 * `drawdownPercent` is measured against the peak inside the window.
 */
export const valueHistoryQueryOptions = (period: ValueHistoryPeriod, shareholderId?: number) =>
  queryOptions<SnapshotPoint[]>({
    queryKey: portfolioKeys.valueHistory(period, shareholderId),
    queryFn: () => portfolioService.valueHistory(period, shareholderId),
  })

/**
 * `placeholderData` lives here, not on the factory above, and is scoped to
 * the SAME shareholder on purpose. `keepPreviousData` on the factory used to
 * keep painting whichever profile's curve was on screen, dimmed as "Updating
 * chart…", while a DIFFERENT profile's request was still in flight — the
 * key's scope (`portfolioKeys.book(shareholderId)`, index 2 of the tuple
 * `portfolioKeys.valueHistory` builds) changes on a profile switch exactly
 * like the period does, and a plain `keepPreviousData` cannot tell those two
 * key changes apart. Reading the scope back off `portfolioKeys.book(...)`
 * rather than comparing `shareholderId` directly keeps this in step with
 * however that key is actually built (its own `?? 'primary'` sentinel
 * included) instead of re-deriving the same rule a second time.
 */
export const useValueHistory = (
  period: ValueHistoryPeriod = DEFAULT_VALUE_HISTORY_PERIOD,
  shareholderId?: number,
): UseQueryResult<SnapshotPoint[]> => {
  const scope = portfolioKeys.book(shareholderId)[2]

  return useQuery<SnapshotPoint[]>({
    ...valueHistoryQueryOptions(period, shareholderId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === scope ? previousData : undefined,
  })
}

/**
 * Return measures, sector contribution and the top five movers each way.
 *
 * All three return measures can be `null`, and two of them ARE null for every
 * member until the snapshot cron runs. `null` means "cannot be computed" and
 * must render as an em-dash with a "not enough history yet" explanation —
 * never as 0 %, which is a real and very different number. The branded `Money`
 * type makes `?? 0` a compile error rather than a wrong figure; see
 * `isComputedMeasure` in `@/schemas/portfolio` for the gate.
 *
 * `returns.turnover` is a RATIO. Do not scale it by 100 to render it.
 */
export const useAnalytics = (shareholderId?: number): UseQueryResult<AnalyticsReport> =>
  useQuery<AnalyticsReport>({
    queryKey: portfolioKeys.analytics(shareholderId),
    queryFn: () => portfolioService.analytics(shareholderId),
  })

/**
 * The six health checks and the three counters over them.
 *
 * There is NO score to read — no 0–100, no grade. If a design shows one, the
 * screen derives it from `steady / checks.length` and owns that choice.
 *
 * `checks.length === 0` means the member has no profile at all; an empty but
 * real profile answers with six `steady` checks. Two different empties.
 */
export const healthQueryOptions = (shareholderId?: number) =>
  queryOptions<HealthReport>({
    queryKey: portfolioKeys.health(shareholderId),
    queryFn: () => portfolioService.health(shareholderId),
  })

export const useHealth = (shareholderId?: number): UseQueryResult<HealthReport> =>
  useQuery<HealthReport>(healthQueryOptions(shareholderId))

/**
 * Dividends received per company, plus announced corporate actions on
 * companies still held.
 *
 * `totalGross`/`totalTax`/`totalNet` are `"0.00"` on an empty book — where `summary.dividendReceived`
 * is `null` in the identical state. Same figure, opposite empty semantics.
 */
export const useIncome = (shareholderId?: number): UseQueryResult<IncomeReport> =>
  useQuery<IncomeReport>({
    queryKey: portfolioKeys.income(shareholderId),
    queryFn: () => portfolioService.income(shareholderId),
  })

// ── Invalidation helpers ─────────────────────────────────────────────────────

/** Every read for one profile: holdings, summary, overview, ledger, P&L. */
const invalidateBook = (client: QueryClient, shareholderId?: number) =>
  client.invalidateQueries({ queryKey: portfolioKeys.book(shareholderId) })

const invalidateShareholders = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: portfolioKeys.shareholders() })

/** The whole domain. Only for mutations whose blast radius cannot be named. */
const invalidateEverything = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: portfolioKeys.all })

// ── Profile writes ───────────────────────────────────────────────────────────

export const useCreateProfile = (): UseMutationResult<
  ShareholderProfile,
  Error,
  ProfileCreateDraft
> => {
  const client = useQueryClient()
  return useMutation<ShareholderProfile, Error, ProfileCreateDraft>({
    mutationFn: (draft) => portfolioService.createProfile(draft),
    onSuccess: async () => {
      await Promise.all([invalidateShareholders(client), invalidateBook(client)])
    },
  })
}

export const useUpdateProfile = (
  id: number,
): UseMutationResult<ShareholderProfile, Error, ProfileUpdateDraft> => {
  const client = useQueryClient()
  return useMutation<ShareholderProfile, Error, ProfileUpdateDraft>({
    mutationFn: (draft) => portfolioService.updateProfile(id, draft),
    // Name, type and demat only — no row, total or ledger entry moves.
    onSuccess: async () => {
      await invalidateShareholders(client)
    },
  })
}

/** What `useDeleteProfile` is handed. `force` defaults to the safe path. */
export interface ProfileDeleteInput {
  id: number
  /**
   * `true` sends `?force=true`, which CASCADES: every buy, sell, dividend and
   * WACC row the profile owns is destroyed with it, irreversibly. Only ever
   * set from an explicit destructive confirmation.
   */
  force?: boolean
}

/**
 * Delete a profile. Two paths, and the first one has not changed.
 *
 * Without `force`, a profile that owns any transaction is still refused with
 * 409 `SHAREHOLDER_HAS_TRANSACTIONS` and the row survives — so the existing
 * confirm dialog is still correct. What is new is that the refusal now has
 * somewhere to go: on that 409, a second, destructive confirmation may retry
 * with `force: true`.
 *
 * ⚠️ Neither the 409 nor the success body says HOW MANY transactions are at
 * stake — the response is `{id, deleted: true}` with no count, forced or not.
 * A dialog that wants to say "this will permanently delete 42 transactions"
 * must count them from `/transactions/` itself. Do not invent the number, and
 * do not read `imported`-style feedback into a response that has none.
 *
 * Invalidates the whole domain either way: the deleted profile's scope is
 * garbage, and deleting the primary promotes the oldest remaining profile, so
 * a scope that is not named in the request also moves.
 */
export const useDeleteProfile = (): UseMutationResult<
  DeletedProfile,
  Error,
  ProfileDeleteInput
> => {
  const client = useQueryClient()
  return useMutation<DeletedProfile, Error, ProfileDeleteInput>({
    mutationFn: ({ id, force }) => portfolioService.deleteProfile(id, force ?? false),
    onSuccess: async () => {
      await invalidateEverything(client)
    },
  })
}

export const useSetPrimary = (): UseMutationResult<ShareholderProfile, Error, number> => {
  const client = useQueryClient()
  return useMutation<ShareholderProfile, Error, number>({
    mutationFn: (id) => portfolioService.setPrimary(id),
    onSuccess: async () => {
      await Promise.all([invalidateShareholders(client), invalidateBook(client)])
    },
  })
}

// ── Transaction writes ───────────────────────────────────────────────────────
//
// All four take the profile to write to, and it travels as a QUERY parameter —
// see the note at the top of portfolioService.ts. Each invalidates exactly that
// profile's book.

export const useRecordBuy = (
  shareholderId?: number,
): UseMutationResult<BuyReceipt, Error, BuyDraft> => {
  const client = useQueryClient()
  return useMutation<BuyReceipt, Error, BuyDraft>({
    mutationFn: (draft) => portfolioService.recordBuy(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

/**
 * 400 `INSUFFICIENT_QUANTITY` when the sale exceeds what is held — and the
 * error does not say how many units there are. The book is invalidated on
 * SUCCESS only; on that failure the caller should refetch holdings itself
 * (`needsHoldingsRefetch` in ./errors) because the cap it validated against is
 * what turned out to be stale.
 */
export const useRecordSell = (
  shareholderId?: number,
): UseMutationResult<SellReceipt, Error, SellDraft> => {
  const client = useQueryClient()
  return useMutation<SellReceipt, Error, SellDraft>({
    mutationFn: (draft) => portfolioService.recordSell(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

/**
 * A cash dividend skips the position rebuild — no units move — but it does
 * change `summary.dividendReceived`, so it invalidates the same scope as the
 * other three rather than the ledger alone.
 */
export const useRecordCashDividend = (
  shareholderId?: number,
): UseMutationResult<DividendReceipt, Error, CashDividendDraft> => {
  const client = useQueryClient()
  return useMutation<DividendReceipt, Error, CashDividendDraft>({
    mutationFn: (draft) => portfolioService.recordCashDividend(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

/**
 * Bonus units are FREE. They dilute the WACC rather than adding cost, so the
 * summary's average and every weight move even though nothing was paid — which
 * is precisely why this invalidates the whole book and not just the ledger.
 */
export const useRecordBonus = (
  shareholderId?: number,
): UseMutationResult<BuyReceipt, Error, BonusDraft> => {
  const client = useQueryClient()
  return useMutation<BuyReceipt, Error, BonusDraft>({
    mutationFn: (draft) => portfolioService.recordBonus(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

// ── Import ───────────────────────────────────────────────────────────────────

/**
 * Parse a portal export and show what WOULD be saved. Writes nothing, so it
 * invalidates nothing.
 *
 * The preview hands back NO TOKEN — no id, no draft, no staged file. The trades
 * are saved by posting the reviewed rows back (`useImportSave`), so they need no
 * `File`; only a WACC report, applied afterwards through `useImportCommit`, has
 * to keep its `File` in component state until then. A reload loses it and the
 * member has to pick the file again.
 *
 * A mutation rather than a query on purpose: it takes a file, it is fired by a
 * button, and it must never be retried or refetched on window focus with a
 * `File` whose input the member has since changed.
 */
export const useImportPreview = (
  shareholderId?: number,
): UseMutationResult<ImportPreview, Error, ImportDraft> =>
  useMutation<ImportPreview, Error, ImportDraft>({
    mutationFn: (draft) => portfolioService.importPreview(draft, shareholderId),
  })

/**
 * Save the reviewed trades as JSON, then refetch that profile's whole book.
 *
 * ⚠️ `onSuccess` does NOT mean "everything imported". The route answers 201
 * whatever happens — including `imported: 0` with every row in `skipped` (a
 * re-import lands every row there as `alreadyInPortfolio`) — so the success
 * branch must read `result.imported` and `result.skipped` and say what actually
 * landed. A refused row is a 400 instead, with per-row detail in
 * `portfolioFailure(error).rowErrors`.
 *
 * The invalidation is the whole `book(...)` scope because one save can add
 * buys, sells, bonus lots and cash dividends at once: holdings, summary,
 * overview, transactions, profit-loss, analytics, income and health all move
 * together. Under-invalidating here leaves the member reading "42 imported"
 * above their pre-import portfolio, which is the one outcome worth a refetch
 * of everything.
 */
export const useImportSave = (
  shareholderId?: number,
): UseMutationResult<ImportResult, Error, ImportSaveDraft> => {
  const client = useQueryClient()
  return useMutation<ImportResult, Error, ImportSaveDraft>({
    mutationFn: (draft) => portfolioService.importSave(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

/**
 * Upload one export and save it as parsed, then refetch that profile's whole
 * book. The WACC overlay step: the report is sent on its own, after
 * `useImportSave`, and re-parsed against the book as that save left it.
 *
 * ⚠️ `onSuccess` does NOT mean "everything imported" — as `useImportSave`. For
 * the overlay, `imported` counts the scrips whose average cost was set, a scrip
 * not held is in `skipped`, and `uncovered` lists the held scrips it missed.
 *
 * Same invalidation as `useImportSave`: an average cost moves holdings, the
 * summary's totals, profit-loss, analytics and health — the whole `book(...)`.
 */
export const useImportCommit = (
  shareholderId?: number,
): UseMutationResult<ImportResult, Error, ImportDraft> => {
  const client = useQueryClient()
  return useMutation<ImportResult, Error, ImportDraft>({
    mutationFn: (draft) => portfolioService.importCommit(draft, shareholderId),
    onSuccess: async () => {
      await invalidateBook(client, shareholderId)
    },
  })
}

// ── Transaction edit and delete ──────────────────────────────────────────────

/** What `useEditTransaction` is handed: the row's id plus its per-group body. */
export interface TransactionEditInput {
  id: number
  draft: TransactionEditDraft
}

/**
 * Edit a transaction. Answers with the ACK only — never the updated row — so
 * there is nothing to patch into the cache even if we wanted to.
 *
 * Invalidates the WHOLE domain. Neither the request nor the ack names a
 * profile, so the profile whose book moved cannot be identified; and an edit
 * replays FIFO and reprices every later sell, so the rows that moved are not
 * only the one edited.
 */
export const useEditTransaction = (): UseMutationResult<
  UpdatedTransaction,
  Error,
  TransactionEditInput
> => {
  const client = useQueryClient()
  return useMutation<UpdatedTransaction, Error, TransactionEditInput>({
    mutationFn: ({ id, draft }) => portfolioService.editTransaction(id, draft),
    onSuccess: async () => {
      await invalidateEverything(client)
    },
  })
}

/** What `useDeleteTransaction` is handed. `group` is the union, never a label. */
export interface TransactionDeleteInput {
  group: TransactionGroup
  id: number
}

/**
 * Delete a transaction — 200 with a body, not 204.
 *
 * Deleting a sell RESTORES the units; deleting a buy whose units have already
 * been sold is refused with 400 `INSUFFICIENT_QUANTITY`. Invalidates the whole
 * domain, for the same reason as the edit above.
 */
export const useDeleteTransaction = (): UseMutationResult<
  DeletedTransaction,
  Error,
  TransactionDeleteInput
> => {
  const client = useQueryClient()
  return useMutation<DeletedTransaction, Error, TransactionDeleteInput>({
    mutationFn: ({ group, id }) => portfolioService.deleteTransaction(group, id),
    onSuccess: async () => {
      await invalidateEverything(client)
    },
  })
}
